import React from 'react';
import Typography from '@material-ui/core/Typography';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { Button } from '@michelin/acid-components';
import { usePermissions, useTranslation } from '@michelin/central-provider';
import { BillingProfileOVM, ContactListingOVM } from '@michelin/fcp-view-models';
import { getOptionLabel } from '@michelin/select-options-provider';
import { useGetBillingProfileDetail, useGetBillingProfileList } from 'hooks/useBillingProfile';
import { useContactsList } from 'hooks/useContacts';
import { AuthorizationService, BillingProfileTypes, BillingServiceTypes } from 'modules/Billing/Profiles/types';
import { ServiceOfferIds, getBaseUrl } from 'prefs-and-service-offers';
import { useHistory, useParams } from 'react-router-dom';
import { AlertBanner, AreaHeader, OpenFullButton, Panel, StatusTone, ToggleRow, ValueRow, WhyItMatters } from './components';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    sectionHeader: {
      fontSize: '0.85rem',
      fontWeight: 700,
      color: theme.palette.text.primary,
      margin: '0.25rem 0 0.75rem',
    },
    sectionSubHeader: {
      fontSize: '0.78rem',
      color: theme.palette.text.secondary,
      marginBottom: '0.75rem',
    },
    limitsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '0.75rem',
    },
    limitCard: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 10,
      padding: '1rem 1.1rem',
      backgroundColor: theme.palette.background.paper,
    },
    limitLabel: {
      fontSize: '0.68rem',
      fontWeight: 700,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
      color: theme.palette.text.secondary,
      marginBottom: 6,
    },
    limitValue: {
      fontSize: '1.5rem',
      fontWeight: 700,
      color: theme.palette.primary.main,
      lineHeight: 1.1,
    },
    limitValueDanger: {
      fontSize: '1.1rem',
      fontWeight: 700,
      color: '#c0392b',
      lineHeight: 1.1,
    },
    limitValueMuted: {
      fontSize: '1.1rem',
      fontWeight: 600,
      color: theme.palette.text.secondary,
      lineHeight: 1.1,
    },
    limitHint: {
      marginTop: 6,
      fontSize: '0.78rem',
      color: theme.palette.text.secondary,
    },
    limitHintDanger: {
      marginTop: 6,
      fontSize: '0.78rem',
      color: '#c0392b',
    },
    inlineButton: {
      textTransform: 'none',
    },
  }),
);

interface ServiceSectionConfig {
  serviceType: BillingServiceTypes;
  title: string;
  subtitle: string;
}

interface LimitCardData {
  key: string;
  label: string;
  value: string;
  hint: string;
  missing: boolean;
}

interface BillingServiceSectionData {
  serviceType: BillingServiceTypes;
  title: string;
  subtitle: string;
  profile: BillingProfileOVM | null;
  profileTypeLabel: string | undefined;
  primaryApproverName: string;
  secondaryApproverName: string;
  missingReasons: string[];
  limits: LimitCardData[];
}

const ERS_MANAGER_ROLE_AREA_VALUE = 'ers_manager';
const PO_ISSUER_ROLE_AREA_VALUE = 'po_issuer';
const PRIMARY_LEVEL_VALUE = 'primary';
const SECONDARY_LEVEL_VALUE = 'secondary';
const TIRE_SERVICE_VALUE = 'tire';
const MECH_SERVICE_VALUE = 'mechanical';

const SERVICE_SECTIONS: ServiceSectionConfig[] = [
  {
    serviceType: BillingServiceTypes.TIRE_WHEEL,
    title: 'Tire Billing & Approval',
    subtitle: 'Profile-level PO and pre-approval settings for tire and wheel service.',
  },
  {
    serviceType: BillingServiceTypes.MECHANICAL,
    title: 'Mechanical Billing & Approval',
    subtitle: 'Profile-level PO and pre-approval settings for mechanical service.',
  },
];

function getProfileRecencyTimestamp(profile: BillingProfileOVM): number {
  const dateValue = profile.update_date || profile.created_date;
  if (!dateValue) return 0;
  const parsed = Date.parse(dateValue);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function pickProfileForService(profiles: BillingProfileOVM[], serviceType: BillingServiceTypes): BillingProfileOVM | null {
  const normalizedServiceType = String(serviceType).trim().toLowerCase();
  const matches = profiles.filter(
    (profile) => String(profile.general_information?.service_type || '').trim().toLowerCase() === normalizedServiceType,
  );
  if (!matches.length) return null;

  return matches.reduce<BillingProfileOVM | null>((latest, profile) => {
    if (!latest) return profile;
    const latestTs = getProfileRecencyTimestamp(latest);
    const profileTs = getProfileRecencyTimestamp(profile);
    if (profileTs > latestTs) return profile;
    if (profileTs < latestTs) return latest;
    // If timestamps are equal/missing, prefer a default profile if available.
    if (!!profile.general_information?.default && !latest.general_information?.default) return profile;
    return latest;
  }, null);
}

function formatCount(value: number | null | undefined): string {
  return Number.isFinite(value) ? String(value) : 'Not set';
}

function formatCurrency(value: number | null | undefined): string {
  if (!Number.isFinite(value)) return 'Not set';
  return `$${Number(value).toLocaleString()}`;
}

function formatAuthorizationMethod(value: string | null | undefined, t: (key: string) => string): string {
  if (!value || value === 'Select') return t('Not set');
  return (
    getOptionLabel('required_authorization_to_begin_service_ers', value) ||
    getOptionLabel('required_authorization_to_begin_service_onsite', value) ||
    value
  );
}

function formatAuthorizedContactTypes(values: string[] | null | undefined, t: (key: string) => string): string {
  if (!values || values.length === 0) return t('Not set');
  const fallbackLabels: Record<string, string> = {
    po_issuer: t('PO Issuer'),
    ers_manager: t('ERS Manager'),
  };
  return values.map((value) => fallbackLabels[value] || value).join(', ');
}

function getOrderedApproverRoles(profile: BillingProfileOVM, primary: boolean): string[] {
  const ersValues = primary
    ? profile.ers_purchasing_procedures?.pri_ers_authorized_contact_type
    : profile.ers_purchasing_procedures?.sec_ers_authorized_contact_type;
  const onsiteValues = primary
    ? profile.onsite_purchasing_procedures?.pri_ers_authorized_contact_type
    : profile.onsite_purchasing_procedures?.sec_ers_authorized_contact_type;
  return Array.from(new Set([...(ersValues || []), ...(onsiteValues || [])]));
}

function resolveApproverRoles(profile: BillingProfileOVM): { primaryRole?: string; secondaryRoles: string[] } {
  const primaryRoles = getOrderedApproverRoles(profile, true);
  const primaryRole = primaryRoles[0];
  const secondaryRoles = getOrderedApproverRoles(profile, false);

  return { primaryRole, secondaryRoles };
}

function hasRoleAreaAtLevelForService(
  contact: ContactListingOVM,
  roleArea: string,
  level: string,
  service: string,
  locationHashKey?: string,
): boolean {
  return !!contact.contact_types?.some(
    (contactType) =>
      contactType.service === service &&
      contactType.role_areas?.some(
        (contactTypeRoleArea) =>
          contactTypeRoleArea.role_area === roleArea &&
          contactTypeRoleArea.levels?.some(
            (contactTypeLevel) =>
              contactTypeLevel.level === level &&
              (typeof locationHashKey === 'undefined' || contactTypeLevel.location === locationHashKey),
          ),
      ),
  );
}

function findContactsForRoleAtLevel(
  contacts: ContactListingOVM[],
  roleArea: string | undefined,
  level: string,
  service: string,
  locationHashKey?: string,
): ContactListingOVM[] {
  if (!roleArea) return [];

  return contacts.filter((item) => hasRoleAreaAtLevelForService(item, roleArea, level, service, locationHashKey));
}

function getContactDisplayName(contact: ContactListingOVM | undefined, t: (key: string) => string): string {
  if (!contact) return t('Not set');

  const first = (contact.first_name || '').trim();
  const last = (contact.last_name || '').trim();
  const fullName = `${first} ${last}`.trim();
  return fullName || t('Not set');
}

function findContactNameForRole(
  contacts: ContactListingOVM[],
  roleArea: string | undefined,
  level: string,
  service: string,
  t: (key: string) => string,
  locationHashKey?: string,
): string {
  const firstContact = findContactsForRoleAtLevel(contacts, roleArea, level, service, locationHashKey)[0];
  return getContactDisplayName(firstContact, t);
}

function findSecondaryContactName(
  contacts: ContactListingOVM[],
  primaryRole: string | undefined,
  secondaryRoles: string[],
  service: string,
  t: (key: string) => string,
  locationHashKey?: string,
): string {
  const primaryContact = findContactsForRoleAtLevel(
    contacts,
    primaryRole,
    PRIMARY_LEVEL_VALUE,
    service,
    locationHashKey,
  )[0];
  const primaryName = getContactDisplayName(primaryContact, t);

  for (const role of secondaryRoles) {
    const preferredLevelMatches = findContactsForRoleAtLevel(
      contacts,
      role,
      PRIMARY_LEVEL_VALUE,
      service,
      locationHashKey,
    );
    const fallbackLevelMatches = findContactsForRoleAtLevel(
      contacts,
      role,
      SECONDARY_LEVEL_VALUE,
      service,
      locationHashKey,
    );

    const candidates = [...preferredLevelMatches, ...fallbackLevelMatches.filter((item) => !preferredLevelMatches.includes(item))];
    const uniqueCandidate = candidates.find((candidate) => {
      const candidateName = getContactDisplayName(candidate, t);
      return candidate !== primaryContact && candidateName !== primaryName;
    });

    if (uniqueCandidate) return getContactDisplayName(uniqueCandidate, t);
  }

  return t('Not set');
}

function getMethodToRequestServiceEntries(
  profile: BillingProfileOVM,
  primary: boolean,
): Array<{ method: string; phoneNumber: string }> {
  const ersMethod = primary
    ? profile.ers_purchasing_procedures?.primary_method_to_request_service
    : profile.ers_purchasing_procedures?.secondary_method_to_request_service;
  const onsiteMethod = primary
    ? profile.onsite_purchasing_procedures?.primary_method_to_request_service
    : profile.onsite_purchasing_procedures?.secondary_method_to_request_service;
  const ersPhone = primary ? profile.ers_purchasing_procedures?.pri_phone_number : profile.ers_purchasing_procedures?.sec_phone_number;
  const onsitePhone = primary
    ? profile.onsite_purchasing_procedures?.pri_phone_number
    : profile.onsite_purchasing_procedures?.sec_phone_number;

  return [
    { method: String(ersMethod || '').trim().toLowerCase(), phoneNumber: String(ersPhone || '').trim() },
    { method: String(onsiteMethod || '').trim().toLowerCase(), phoneNumber: String(onsitePhone || '').trim() },
  ].filter((entry) => !!entry.method && entry.method !== 'select');
}

function resolveContactDisplayValue(
  profile: BillingProfileOVM,
  primary: boolean,
  fleetContactName: string,
  t: (key: string) => string,
): string {
  const entries = getMethodToRequestServiceEntries(profile, primary);

  // If no explicit method is defined, keep legacy fleet-contact resolution behavior.
  if (entries.length === 0) return fleetContactName;

  for (const entry of entries) {
    if (entry.method === AuthorizationService.driver) return t('Driver');

    if (entry.method === AuthorizationService.break_down_phone_number && entry.phoneNumber) {
      return entry.phoneNumber;
    }

    if (entry.method === AuthorizationService.fleet_contact) {
      return fleetContactName;
    }
  }

  return fleetContactName;
}

function getLimitCards(profile: BillingProfileOVM, serviceType: BillingServiceTypes, t: (key: string) => string): LimitCardData[] {
  const profileType = profile.general_information?.profile_type;
  const ersMaxDollars = profile.approvals?.ers_pre_approvals_limits?.maximum_dollar_amount;
  const onsiteMaxDollars = profile.approvals?.onsite_pre_approvals_limits?.maximum_dollar_amount;
  const ersMaxTires = profile.approvals?.ers_pre_approvals_limits?.maximum_num_of_tires;
  const onsiteMaxTires = profile.approvals?.onsite_pre_approvals_limits?.maximum_num_of_tires;
  const maxTires = Math.max(Number(ersMaxTires || 0), Number(onsiteMaxTires || 0));

  const emergencyMissing =
    profileType !== BillingProfileTypes.onsite && (!Number.isFinite(ersMaxDollars) || Number(ersMaxDollars) <= 0);
  const standardMissing =
    profileType !== BillingProfileTypes.ers && (!Number.isFinite(onsiteMaxDollars) || Number(onsiteMaxDollars) <= 0);
  const maxTiresMissing =
    serviceType === BillingServiceTypes.TIRE_WHEEL
      ? profileType !== BillingProfileTypes.onsite && maxTires <= 0
      : maxTires <= 0;

  if (serviceType === BillingServiceTypes.MECHANICAL) {
    return [
      {
        key: 'standard-repair',
        label: t('Standard repair'),
        value: formatCurrency(onsiteMaxDollars),
        hint: t('Per event'),
        missing: standardMissing,
      },
      {
        key: 'emergency-limit',
        label: t('Emergency limit'),
        value: formatCurrency(ersMaxDollars),
        hint: t('Per event'),
        missing: emergencyMissing,
      },
    ];
  }

  return [
    {
      key: 'standard-repair',
      label: t('Standard repair'),
      value: formatCurrency(onsiteMaxDollars),
      hint: t('Per event'),
      missing: standardMissing,
    },
    {
      key: 'max-tires',
      label: t('Max tires'),
      value: maxTires > 0 ? formatCount(maxTires) : t('Not set'),
      hint: t('Per event'),
      missing: maxTiresMissing,
    },
  ];
}

interface BillingApprovalAreaStatusResult {
  headerStatus: { label: string; tone: StatusTone };
  incompleteCount: number;
  sections: BillingServiceSectionData[];
  refreshStatus: () => Promise<any>;
}

export function useBillingApprovalAreaStatus(): BillingApprovalAreaStatusResult {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const { selectedAccount } = useParams<{ selectedAccount: string }>();
  const fleetId = permissions.location?.customer_number || selectedAccount || '';
  const locationHashKey = permissions.location?.hash_key;
  const customerType = permissions.location?.customer_type;

  const { data: contacts = [] } = useContactsList(
    fleetId,
    [ERS_MANAGER_ROLE_AREA_VALUE, PO_ISSUER_ROLE_AREA_VALUE],
    customerType,
  );

  const { data: billingProfilesData = [], refetch: refetchBillingProfiles } = useGetBillingProfileList({ fleetId });

  const billingProfiles = React.useMemo(
    () => (Array.isArray(billingProfilesData) ? billingProfilesData : []),
    [billingProfilesData],
  );

  const tireListProfile = React.useMemo(
    () => pickProfileForService(billingProfiles, BillingServiceTypes.TIRE_WHEEL),
    [billingProfiles],
  );

  const mechanicalListProfile = React.useMemo(
    () => pickProfileForService(billingProfiles, BillingServiceTypes.MECHANICAL),
    [billingProfiles],
  );

  const {
    data: tireDetailProfile,
    refetch: refetchTireDetailProfile,
  } = useGetBillingProfileDetail({
    fleetId,
    profileId: tireListProfile?.id || '',
    enabled: !!tireListProfile?.id,
  });

  const {
    data: mechanicalDetailProfile,
    refetch: refetchMechanicalDetailProfile,
  } = useGetBillingProfileDetail({
    fleetId,
    profileId: mechanicalListProfile?.id || '',
    enabled: !!mechanicalListProfile?.id,
  });

  const tireProfile = tireDetailProfile || tireListProfile || null;
  const mechanicalProfile = mechanicalDetailProfile || mechanicalListProfile || null;

  const sections = React.useMemo(() => {
    const selectedProfilesByService: Record<BillingServiceTypes, BillingProfileOVM | null> = {
      [BillingServiceTypes.TIRE_WHEEL]: tireProfile,
      [BillingServiceTypes.MECHANICAL]: mechanicalProfile,
    };

    return SERVICE_SECTIONS.map((section) => {
      const profile = selectedProfilesByService[section.serviceType];
      if (!profile) {
        return {
          ...section,
          profile: null,
          profileTypeLabel: t('Not set'),
          primaryApproverName: t('Not set'),
          secondaryApproverName: t('Not set'),
          missingReasons: [t('No billing profile configured.')],
          limits: [],
        };
      }

      const { primaryRole, secondaryRoles } = resolveApproverRoles(profile);
      const service = section.serviceType === BillingServiceTypes.MECHANICAL ? MECH_SERVICE_VALUE : TIRE_SERVICE_VALUE;
      const primaryFleetContactName = findContactNameForRole(
        contacts,
        primaryRole,
        PRIMARY_LEVEL_VALUE,
        service,
        t,
        locationHashKey,
      );
      const secondaryFleetContactName = findSecondaryContactName(
        contacts,
        primaryRole,
        secondaryRoles,
        service,
        t,
        locationHashKey,
      );
      const primaryApproverName = resolveContactDisplayValue(profile, true, primaryFleetContactName, t);
      const secondaryApproverName = resolveContactDisplayValue(profile, false, secondaryFleetContactName, t);

      const limits = getLimitCards(profile, section.serviceType, t);
      const missingLimitCount = limits.filter((limit) => limit.missing).length;
      const missingReasons =
        missingLimitCount > 0
          ? [
              t('{{count}} pre-approval limit(s) need to be configured.', {
                count: missingLimitCount,
              }),
            ]
          : [];

      return {
        ...section,
        profile,
        profileTypeLabel:
          getOptionLabel('profile_types', profile.general_information?.profile_type || '') ||
          profile.general_information?.profile_type ||
          t('Not set'),
        primaryApproverName,
        secondaryApproverName,
        missingReasons,
        limits,
      };
    });
  }, [contacts, locationHashKey, mechanicalProfile, t, tireProfile]);

  const incompleteCount = sections.reduce((count, section) => count + section.missingReasons.length, 0);
  const headerStatus: { label: string; tone: StatusTone } =
    incompleteCount > 0
      ? { label: t('{{count}} incomplete', { count: incompleteCount }), tone: 'danger' }
      : { label: t('Complete'), tone: 'success' };

  const refreshStatus = React.useCallback(() => {
    return Promise.all([refetchBillingProfiles(), refetchTireDetailProfile(), refetchMechanicalDetailProfile()]);
  }, [refetchBillingProfiles, refetchTireDetailProfile, refetchMechanicalDetailProfile]);

  return {
    headerStatus,
    incompleteCount,
    sections,
    refreshStatus,
  };
}

export function BillingApprovalSubArea() {
  const { t } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const permissions = usePermissions();
  const { headerStatus, incompleteCount, sections } = useBillingApprovalAreaStatus();

  const urbanOnlyFleet = !!(
    permissions.location &&
    permissions.location.is_urban &&
    !permissions.location.is_commercial &&
    !permissions.location.override_is_commercial
  );
  const legacyServiceOffer = urbanOnlyFleet ? ServiceOfferIds.bulkOrder : ServiceOfferIds.onCall;
  const legacyBasePath = `${getBaseUrl(false)}/${legacyServiceOffer}`;

  const openLegacyBillings = () => history.push(`${legacyBasePath}/billings/profiles`);
  const openLegacyBillingProfile = (profileId: string) => history.push(`${legacyBasePath}/billing/${profileId}/view`);
  const tireSection = sections.find((section) => section.serviceType === BillingServiceTypes.TIRE_WHEEL) || null;
  const mechanicalSection = sections.find((section) => section.serviceType === BillingServiceTypes.MECHANICAL) || null;

  const renderSectionPanels = (section: BillingServiceSectionData | null) => {
    if (!section) return null;

    const profile = section.profile;
    const profileId = profile?.id;

    if (!profile) {
      return (
        <AlertBanner
          message={
            <span>
              <strong>{t('No billing profile found.')}</strong>{' '}
              {t('Create or assign a profile for this service to configure approval and pre-approval limits.')}
            </span>
          }
          action={
            <Button
              variant="contained"
              color="primary"
              size="small"
              className={classes.inlineButton}
              onClick={openLegacyBillings}
            >
              {t('Open Billing Profiles')}
            </Button>
          }
        />
      );
    }

    return (
      <>
        <Panel
          title={
            t(
              section.serviceType === BillingServiceTypes.MECHANICAL
                ? 'Mechanical Invoicing & approvers'
                : 'Tire Invoicing & approvers',
            )
          }
          action={
            <Button
              variant="contained"
              size="small"
              className={classes.inlineButton}
              onClick={() => profileId && openLegacyBillingProfile(profileId)}
            >
              {t('View full profile')}
            </Button>
          }
        >
          <ToggleRow
            label={t('PO required for invoicing')}
            description={t('No invoice without a PO number when enabled.')}
            checked={!!profile.general_information?.po_required_for_service}
          />
          <ValueRow label={t('Primary approval contact')} value={section.primaryApproverName} />
          <ValueRow
            label={t('Secondary approval contact')}
            value={section.secondaryApproverName}
            valueTone={section.secondaryApproverName === t('Not set') ? 'danger' : undefined}
          />
        </Panel>

        <Panel
          title={
            t(
              section.serviceType === BillingServiceTypes.MECHANICAL
                ? 'Mechanical Pre-approval limits'
                : 'Tire Pre-approval limits',
            )
          }
        >
          <div className={classes.limitsGrid}>
            {section.limits.map((limit) => {
              const valueClassName = limit.missing
                ? classes.limitValueDanger
                : limit.value === t('Not set')
                ? classes.limitValueMuted
                : classes.limitValue;

              return (
                <div className={classes.limitCard} key={limit.key}>
                  <Typography className={classes.limitLabel}>{limit.label}</Typography>
                  <Typography className={valueClassName}>{limit.value}</Typography>
                  <Typography className={limit.missing ? classes.limitHintDanger : classes.limitHint}>
                    {limit.missing ? `\u26A0 ${t('Needs update')}` : limit.hint}
                  </Typography>
                </div>
              );
            })}
          </div>
        </Panel>
      </>
    );
  };

  return (
    <>
      <AreaHeader
        icon={<span aria-hidden>&#128176;</span>}
        iconColor="#fdf3c9"
        title={t('Billing & Approval')}
        subtitle={t('PO rules, approval contacts, and the dollar / tire ceilings before we pause to ask.')}
        status={headerStatus}
      />

      {incompleteCount > 0 && (
        <AlertBanner
          message={
            <span>
              <strong>{t('Billing profiles need attention.')}</strong>{' '}
              {t('One or more Tire / Mechanical billing profiles are missing required limits or are not configured.')}
            </span>
          }
          action={
            <Button variant="contained" color="primary" size="small" className={classes.inlineButton} onClick={openLegacyBillings}>
              {t('Open Billing Profiles')}
            </Button>
          }
        />
      )}

      {renderSectionPanels(tireSection)}
      {renderSectionPanels(mechanicalSection)}

      <WhyItMatters>
        {t(
          'Billing profiles define the live PO rules and pre-approval limits used by legacy workflows. Keeping Tire and Mechanical profiles aligned avoids repair and invoicing delays.',
        )}
      </WhyItMatters>

      <OpenFullButton label={t('Open full Approval & Billing')} onClick={openLegacyBillings} />
    </>
  );
}

export default BillingApprovalSubArea;
